/**
 * MedicosAsignadosEditor — Asignación de médicos a una sala con
 * vigencia, rol (titular/suplente) y toggle `usaHorarioSala`.
 *
 * Subcolección: `salas/{salaId}/medicos_sala`.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Stethoscope, UserPlus, X, Clock, CalendarClock, Search, Power } from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import { Badge } from '../ui/Badge';
import { HorarioMedicoModal } from './HorarioMedicoModal';
import styles from './MedicosAsignadosEditor.module.css';

interface MedicoSalaDoc {
  medicoId: string;
  habilitado: boolean;
  rolEnSala?: 'titular' | 'suplente';
  usaHorarioSala?: boolean;
  vigenciaDesde?: { toDate: () => Date };
  vigenciaHasta?: { toDate: () => Date } | null;
  peso?: number;
}

interface Medico {
  id: string;
  nombre?: string;
  apellidos?: string;
  nombreCompleto?: string;
  especialidad?: string;
  activo?: boolean;
  role?: string;
}

const callGestionarMedicosSala = httpsCallable(functions, 'portal_gestionarMedicosSala');

/** Normaliza texto para búsqueda: minúsculas y sin acentos. */
const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/** Nombre visible del médico (nombreCompleto o nombre+apellidos). */
const nombreDe = (m: Medico): string =>
  m.nombreCompleto || `${m.nombre ?? ''} ${m.apellidos ?? ''}`.trim() || m.id;

/** Iniciales para el avatar (máx. 2 letras). */
const inicialesDe = (nombre: string): string => {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
};

export interface MedicosAsignadosEditorProps {
  salaId: string;
}

export function MedicosAsignadosEditor({ salaId }: MedicosAsignadosEditorProps) {
  const { addToast } = useToast();
  const [asignados, setAsignados] = useState<MedicoSalaDoc[]>([]);
  const [disponibles, setDisponibles] = useState<Medico[]>([]);
  const [loadingA, setLoadingA] = useState(true);
  const [loadingD, setLoadingD] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [busquedaDisp, setBusquedaDisp] = useState('');
  const [busquedaAsig, setBusquedaAsig] = useState('');
  const [horarioModalFor, setHorarioModalFor] = useState<{
    medicoId: string;
    nombre: string;
  } | null>(null);

  // ── Suscripciones ──
  useEffect(() => {
    if (!salaId) return;
    const ref = collection(db, `salas/${salaId}/medicos_sala`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const items: MedicoSalaDoc[] = snap.docs.map((d) => ({
          medicoId: d.id,
          ...(d.data() as Omit<MedicoSalaDoc, 'medicoId'>),
        }));
        setAsignados(items);
        setLoadingA(false);
      },
      () => setLoadingA(false),
    );
    return () => unsub();
  }, [salaId]);

  useEffect(() => {
    const q = query(collection(db, 'medicos'), where('activo', '==', true));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Medico[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Medico, 'id'>),
        }));
        setDisponibles(list);
        setLoadingD(false);
      },
      () => setLoadingD(false),
    );
    return () => unsub();
  }, []);

  // ── Maps de ayuda ──
  const disponiblesMap = useMemo(
    () => new Map(disponibles.map((m) => [m.id, m])),
    [disponibles],
  );
  const asignadosMap = useMemo(
    () => new Map(asignados.map((a) => [a.medicoId, a])),
    [asignados],
  );
  const noAsignados = useMemo(
    () => disponibles.filter((m) => !asignadosMap.has(m.id)),
    [disponibles, asignadosMap],
  );

  // ── Listas filtradas por búsqueda (nombre + especialidad, sin acentos) ──
  const noAsignadosFiltrados = useMemo(() => {
    const q = normalizar(busquedaDisp.trim());
    if (!q) return noAsignados;
    return noAsignados.filter((m) =>
      normalizar(`${nombreDe(m)} ${m.especialidad ?? ''}`).includes(q),
    );
  }, [noAsignados, busquedaDisp]);

  const asignadosFiltrados = useMemo(() => {
    const q = normalizar(busquedaAsig.trim());
    if (!q) return asignados;
    return asignados.filter((a) => {
      const m = disponiblesMap.get(a.medicoId);
      const texto = m
        ? `${nombreDe(m)} ${m.especialidad ?? ''}`
        : a.medicoId;
      return normalizar(texto).includes(q);
    });
  }, [asignados, busquedaAsig, disponiblesMap]);

  // ── Acciones ──
  const handleAgregar = async (medicoId: string) => {
    setSubmitting(medicoId);
    try {
      await callGestionarMedicosSala({
        salaId,
        accion: 'agregar',
        medicoUid: medicoId,
      });
      addToast('success', 'Médico asignado');
      setShowPicker(false);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error al asignar');
    } finally {
      setSubmitting(null);
    }
  };

  const handleRemover = async (medicoId: string) => {
    setSubmitting(medicoId);
    try {
      await callGestionarMedicosSala({
        salaId,
        accion: 'remover',
        medicoUid: medicoId,
      });
      addToast('success', 'Médico removido');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error al remover');
    } finally {
      setSubmitting(null);
    }
  };

  /** Deja al médico asignado pero sin contar para la capacidad (sin perder su horario/vigencia). */
  const handleToggleHabilitado = async (medicoId: string, habilitadoActual: boolean) => {
    setSubmitting(medicoId);
    try {
      await callGestionarMedicosSala({
        salaId,
        accion: habilitadoActual ? 'deshabilitar' : 'habilitar',
        medicoUid: medicoId,
      });
      addToast('success', habilitadoActual ? 'Médico deshabilitado' : 'Médico habilitado');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error al cambiar el estado');
    } finally {
      setSubmitting(null);
    }
  };

  const medicoLabel = (medicoId: string): string => {
    const m = disponiblesMap.get(medicoId);
    return m ? nombreDe(m) : medicoId;
  };

  const medicoEspecialidad = (medicoId: string): string | undefined =>
    disponiblesMap.get(medicoId)?.especialidad;

  return (
    <div className={styles.editor}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>
            <Stethoscope size={16} /> Médicos asignados
          </h3>
          <p className={styles.subtitle}>
            Médicos autorizados a atender en esta sala. Cada uno puede heredar
            el horario de la sala o tener uno propio.
          </p>
        </div>
        <button
          type="button"
          className={styles.addBtn}
          onClick={() =>
            setShowPicker((v) => {
              if (v) setBusquedaDisp('');
              return !v;
            })
          }
          disabled={loadingD}
        >
          <UserPlus size={14} /> {showPicker ? 'Cerrar' : 'Asignar médico'}
        </button>
      </div>

      {showPicker && (
        <div className={styles.picker}>
          <div className={styles.searchBox}>
            <Search size={15} className={styles.searchIcon} aria-hidden="true" />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Buscar por nombre o especialidad…"
              aria-label="Buscar entre los médicos disponibles"
              value={busquedaDisp}
              onChange={(e) => setBusquedaDisp(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  noAsignadosFiltrados.length === 1 &&
                  submitting === null
                ) {
                  e.preventDefault();
                  handleAgregar(noAsignadosFiltrados[0].id);
                }
              }}
              autoFocus
            />
            {busquedaDisp && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => setBusquedaDisp('')}
                aria-label="Limpiar búsqueda"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className={styles.pickerHeader}>
            <span>
              {busquedaDisp
                ? `${noAsignadosFiltrados.length} de ${noAsignados.length} disponibles`
                : `${noAsignados.length} médicos disponibles`}
            </span>
          </div>

          {noAsignados.length === 0 ? (
            <p className={styles.empty}>Todos los médicos activos ya están asignados.</p>
          ) : noAsignadosFiltrados.length === 0 ? (
            <p className={styles.empty}>
              Ningún médico coincide con «{busquedaDisp.trim()}».
            </p>
          ) : (
            <ul className={styles.pickerList}>
              {noAsignadosFiltrados.map((m) => {
                const nombre = nombreDe(m);
                return (
                  <li key={m.id} className={styles.pickerItem}>
                    <div className={styles.medicoRow}>
                      <span className={styles.avatar} aria-hidden="true">
                        {inicialesDe(nombre)}
                      </span>
                      <div className={styles.medicoInfo}>
                        <span className={styles.medicoNombre}>{nombre}</span>
                        {m.especialidad && (
                          <span className={styles.medicoEsp}>{m.especialidad}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.btnPicker}
                      onClick={() => handleAgregar(m.id)}
                      disabled={submitting === m.id}
                    >
                      {submitting === m.id ? '…' : 'Agregar'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {!loadingA && asignados.length > 0 && (
        <div className={styles.asignadosBar}>
          <div className={styles.searchBox}>
            <Search size={15} className={styles.searchIcon} aria-hidden="true" />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Filtrar asignados…"
              aria-label="Filtrar entre los médicos asignados"
              value={busquedaAsig}
              onChange={(e) => setBusquedaAsig(e.target.value)}
            />
            {busquedaAsig && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => setBusquedaAsig('')}
                aria-label="Limpiar filtro"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <span className={styles.asignadosCount}>
            {busquedaAsig
              ? `${asignadosFiltrados.length} de ${asignados.length} asignados`
              : `${asignados.length} asignado${asignados.length === 1 ? '' : 's'}`}
          </span>
        </div>
      )}

      <div className={styles.list}>
        {loadingA ? (
          <p className={styles.empty}>Cargando…</p>
        ) : asignados.length === 0 ? (
          <p className={styles.empty}>
            Sin médicos asignados. La sala no podrá abrir hasta tener al menos uno.
          </p>
        ) : asignadosFiltrados.length === 0 ? (
          <p className={styles.empty}>
            Ningún médico asignado coincide con «{busquedaAsig.trim()}».
          </p>
        ) : (
          asignadosFiltrados.map((a) => (
            <div key={a.medicoId} className={styles.item}>
              <div className={styles.itemMain}>
                <div className={styles.medicoRow}>
                  <span className={styles.avatar} aria-hidden="true">
                    {inicialesDe(medicoLabel(a.medicoId))}
                  </span>
                  <div className={styles.medicoInfo}>
                    <span className={styles.medicoNombre}>{medicoLabel(a.medicoId)}</span>
                    {medicoEspecialidad(a.medicoId) && (
                      <span className={styles.medicoEsp}>
                        {medicoEspecialidad(a.medicoId)}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.badges}>
                  <Badge
                    variant={a.habilitado === false ? 'error' : 'success'}
                    size="sm"
                  >
                    {a.habilitado === false ? 'Deshabilitado' : 'Habilitado'}
                  </Badge>
                  {a.rolEnSala === 'suplente' && (
                    <Badge variant="warning" size="sm">Suplente</Badge>
                  )}
                  <Badge variant="neutral" size="sm">
                    <Clock size={10} />{' '}
                    {a.usaHorarioSala === false ? 'Horario propio' : 'Horario sala'}
                  </Badge>
                </div>
              </div>
              <div className={styles.itemActions}>
                <button
                  type="button"
                  className={styles.horarioBtn}
                  onClick={() =>
                    setHorarioModalFor({
                      medicoId: a.medicoId,
                      nombre: medicoLabel(a.medicoId),
                    })
                  }
                  title="Configurar horario del médico en esta sala"
                >
                  <CalendarClock size={14} aria-hidden="true" />
                  <span>Horario</span>
                </button>
                <button
                  type="button"
                  className={styles.horarioBtn}
                  onClick={() => handleToggleHabilitado(a.medicoId, a.habilitado !== false)}
                  disabled={submitting === a.medicoId}
                  title={a.habilitado === false ? 'Habilitar' : 'Deshabilitar'}
                >
                  <Power size={14} aria-hidden="true" />
                  <span>{a.habilitado === false ? 'Habilitar' : 'Deshabilitar'}</span>
                </button>
                <button
                  type="button"
                  className={styles.delBtn}
                  onClick={() => handleRemover(a.medicoId)}
                  disabled={submitting === a.medicoId}
                  title="Remover de la sala"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de horario por médico */}
      {horarioModalFor && (
        <HorarioMedicoModal
          open={true}
          onClose={() => setHorarioModalFor(null)}
          salaId={salaId}
          medicoId={horarioModalFor.medicoId}
          medicoNombre={horarioModalFor.nombre}
        />
      )}
    </div>
  );
}
